import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mic, BookOpen, Music, GraduationCap, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const CABRINI_PHOTO = 'https://d2xsxph8kpxj0f.cloudfront.net/310519663480781145/NkLswLLVuUgByWNCoV9bMA/cabrini-photo_453797dc.webp';

const CREDENTIALS = [
  'Shared the Stage with Les Brown',
  'Founder of The Alchemy Village',
  'Creator of F.U.C.K. T.H.A.T.T.',
  'Author · Music Producer',
];

const TOPICS = [
  { title: 'Wake Up From Your Life', desc: 'A powerful keynote that disrupts complacency and activates conscious living. Cabrini challenges audiences to stop sleepwalking and start creating.', tags: 'Corporate · Schools · Conferences' },
  { title: 'The Law of F.U.C.K. T.H.A.T.T.', desc: 'Finally Understanding Common Knowledge — a transformational framework that teaches decision-making, trust, focus, and inspired action.', tags: 'Keynotes · Workshops · Retreats' },
  { title: 'Be. Do. Have.', desc: 'The sequence most people get backwards. Cabrini reveals why identity must come before action, and action before results.', tags: 'Entrepreneurship · Leadership · Life Coaching' },
  { title: 'You Are the Manifestor', desc: 'A spiritually grounded, practically delivered session on personal responsibility and the power of conscious creation.', tags: "Women's Events · Spiritual Conferences" },
  { title: 'Feeding Your Mind. Body. Soul.', desc: "Drawing from her GangstaChef persona, Cabrini delivers a unique blend of nutrition wisdom, spiritual nourishment, and mental clarity.", tags: 'Wellness · Holistic Health · Women 50+' },
  { title: 'Now What? — For Women 50+', desc: "Designed specifically for women at the threshold of their next chapter — a session of clarity, permission, and powerful new beginnings.", tags: "Women's Conferences · Senior Living · Retreats" },
];

const TESTIMONIALS = [
  { quote: "Cabrini is a magician. She doesn't just speak — she reaches into you and pulls out the version of yourself you forgot existed.", name: 'Dr. Jacqueline', role: 'Village Member' },
  { quote: "I came in confused and left with a decision. That's what she does. She cuts through the noise and gets you to the truth of who you are.", name: 'Marcus T.', role: 'Entrepreneur' },
  { quote: "At 57, I thought my best chapters were behind me. Cabrini showed me I was just at the threshold. Everything changed after that session.", name: 'Sandra W.', role: 'Women 50+ Community' },
];

const PRINCIPLES = ['Make a Decision', 'Trust Your Decision', 'Stay Focused', 'Take Inspired Action', 'Enjoy the Journey'];

const PATHS = [
  { name: 'F.U.C.K. T.H.A.T.T.', desc: 'The flagship philosophy — Finally Understanding Common Knowledge' },
  { name: 'The ME-Treat', desc: 'A retreat to self — rediscover who you are at your core' },
  { name: "GangstaChef's Kitchen", desc: 'Nourishment for Mind · Body · Soul' },
  { name: 'The Fire Circle', desc: 'From Pity Party to Breakthrough — transformation in community' },
  { name: 'Life-Shopping', desc: "Choosing what truly matters and releasing what doesn't" },
  { name: 'F.E.A.R. Path', desc: 'Face Everything And Rise — turning fear into fuel' },
];

export default function BookCabriniPage() {
  const [form, setForm] = useState({ name: '', email: '', org: '', message: '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    toast.success("Inquiry sent! Cabrini's team will be in touch.");
    setForm({ name: '', email: '', org: '', message: '' });
  };

  return (
    <section data-testid="book-cabrini-page" className="bc">
      {/* Hero */}
      <div className="bc-hero" data-testid="bc-hero">
        <div className="bc-hero-inner">
          <div className="bc-hero-photo">
            <img src={CABRINI_PHOTO} alt="Cabrini Schnyder" className="bc-hero-img" data-testid="bc-hero-img" />
          </div>
          <div className="bc-hero-text">
            <p className="bc-hero-label">Life Alchemist · Transformational Speaker · Author</p>
            <h1 className="bc-hero-title" data-testid="bc-hero-title">I am the woman <em>who wakes people up.</em></h1>
            <p className="bc-hero-desc">For over four decades, Cabrini Schnyder has been transforming lived experience into wisdom — and wisdom into transformation for others. She doesn't just teach. She creates experiences that make people <em>see themselves differently.</em></p>
            <div className="bc-hero-cta">
              <a href="#bc-contact" className="village-btn village-btn--primary" data-testid="bc-book-btn">Book Cabrini to Speak</a>
              <a href="#bc-about" className="village-btn village-btn--secondary" data-testid="bc-story-btn">Discover Her Story</a>
            </div>
            <div className="bc-credentials" data-testid="bc-credentials">
              {CREDENTIALS.map((c, i) => (
                <span key={i} className="bc-cred">{c}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* About */}
      <div id="bc-about" className="bc-about" data-testid="bc-about">
        <div className="bc-about-inner">
          <div className="bc-about-photo">
            <img src={CABRINI_PHOTO} alt="Cabrini Schnyder" className="bc-about-img" />
            <blockquote className="bc-about-quote">"The greatest guide you'll ever have is the God in YOU."</blockquote>
          </div>
          <div className="bc-about-text">
            <h2 className="bc-section-title">Meet <em>Cabrini Schnyder</em></h2>
            <p>Lifelong entrepreneur, creator, and transformational teacher — Cabrini has spent over four decades building businesses, guiding others, and turning real-life experience into wisdom that helps people reclaim their lives.</p>
            <p>Known to many as <strong>GangstaChef Cabrini</strong>, she blends her background in cooking, entrepreneurship, spirituality, and personal development to teach what she calls "Food for Thought" — nourishment for the mind, body, and soul.</p>
            <p>She is the creator of <strong>Cabrini's Law of F.U.C.K. T.H.A.T.T.</strong> — a philosophy designed to help people stop sleepwalking through life and start making conscious decisions about who they are, what they want, and why they want it.</p>
            <div className="bc-about-badges">
              <div className="bc-badge"><Mic size={20} /><span>Keynote Speaker</span><small>Shared stage with Les Brown</small></div>
              <div className="bc-badge"><BookOpen size={20} /><span>Published Author</span><small>Life Alchemy in print</small></div>
              <div className="bc-badge"><Music size={20} /><span>Music Producer</span><small>The Alchemist Music (TAM)</small></div>
              <div className="bc-badge"><GraduationCap size={20} /><span>FTU Founder</span><small>F.U.C.K. T.H.A.T.T. University</small></div>
            </div>
          </div>
        </div>
      </div>

      {/* Philosophy */}
      <div className="bc-philosophy" data-testid="bc-philosophy">
        <p className="bc-eyebrow">Core Philosophy</p>
        <h2 className="bc-section-title">Cabrini's Law of <em>F.U.C.K. T.H.A.T.T.</em></h2>
        <p className="bc-philosophy-sub"><strong>Finally Understanding Common Knowledge — Things Happen All The Time.</strong></p>
        <p className="bc-philosophy-note">This is not a slogan. This is a way of living.</p>

        <div className="bc-questions">
          <h3 className="bc-subheading">Governed by 3 Core Questions</h3>
          <div className="bc-questions-grid">
            <div className="bc-question-card"><span className="bc-q-label">Who am I?</span><p>The foundation of all transformation begins with identity.</p></div>
            <div className="bc-question-card"><span className="bc-q-label">What do I want?</span><p>Clarity of desire is the spark that ignites the flame.</p></div>
            <div className="bc-question-card"><span className="bc-q-label">Why do I want it?</span><p>Purpose is the fuel that sustains the journey.</p></div>
          </div>
        </div>

        <div className="bc-principles">
          <h3 className="bc-subheading">Guided by 5 Principles</h3>
          <div className="bc-principles-list">
            {PRINCIPLES.map((p, i) => (
              <div key={i} className="bc-principle">
                <span className="bc-principle-num">0{i + 1}</span>
                <span className="bc-principle-text">{p}</span>
              </div>
            ))}
          </div>
        </div>

        <blockquote className="bc-pullquote" data-testid="bc-pullquote">
          "People don't need more information. They need to remember who they are."
          <cite>— Cabrini Schnyder</cite>
        </blockquote>
      </div>

      {/* Speaking Topics */}
      <div className="bc-topics" data-testid="bc-topics">
        <p className="bc-eyebrow">On Stage & On Purpose</p>
        <h2 className="bc-section-title">Speaking Topics That <em>Transform Rooms</em></h2>
        <p className="bc-topics-intro">Cabrini doesn't deliver presentations. She creates experiences that make audiences see themselves — and their possibilities — in a completely new light.</p>
        <div className="bc-topics-grid">
          {TOPICS.map((t, i) => (
            <div key={i} className="bc-topic-card" data-testid={`bc-topic-${i}`}>
              <h3 className="bc-topic-title">{t.title}</h3>
              <p className="bc-topic-desc">{t.desc}</p>
              <span className="bc-topic-tags">{t.tags}</span>
            </div>
          ))}
        </div>
        <div className="bc-format-badges">
          <span className="bc-format"><Mic size={14} /> Keynote</span>
          <span className="bc-format">Workshop</span>
          <span className="bc-format">Retreat</span>
          <span className="bc-format">Corporate Training</span>
        </div>
      </div>

      {/* Village Ecosystem */}
      <div className="bc-village" data-testid="bc-village">
        <p className="bc-eyebrow">The Transformation Ecosystem</p>
        <h2 className="bc-section-title">Welcome to <em>The Alchemy Village</em></h2>
        <p className="bc-village-intro">More than a community. A living ecosystem where people come to rediscover themselves through conversation, creativity, music, teaching, cooking, spiritual reflection, and personal responsibility.</p>
        <p className="bc-village-audience">Designed especially for people 50+ who are asking one powerful question: <em>"Now what?"</em></p>
        <div className="bc-paths-grid">
          {PATHS.map((p, i) => (
            <div key={i} className="bc-path-item">
              <span className="bc-path-num">0{i + 1}</span>
              <div><strong className="bc-path-name">{p.name}</strong><p className="bc-path-desc">{p.desc}</p></div>
            </div>
          ))}
        </div>
        <Link to="/life-map" className="bc-village-link" data-testid="bc-village-link">
          Explore the Village <ArrowRight size={16} />
        </Link>
      </div>

      {/* Testimonials */}
      <div className="bc-testimonials" data-testid="bc-testimonials">
        <h2 className="bc-section-title">Lives Changed. <em>Minds Awakened.</em></h2>
        <div className="bc-testimonials-grid">
          {TESTIMONIALS.map((t, i) => (
            <blockquote key={i} className="bc-testimonial" data-testid={`bc-testimonial-${i}`}>
              <p className="bc-testimonial-quote">"{t.quote}"</p>
              <cite className="bc-testimonial-cite"><strong>{t.name}</strong><span>{t.role}</span></cite>
            </blockquote>
          ))}
        </div>
      </div>

      {/* Contact / Book */}
      <div id="bc-contact" className="bc-contact" data-testid="bc-contact">
        <h2 className="bc-section-title">Ready to <em>Wake Up Your Audience?</em></h2>
        <p className="bc-contact-intro">Cabrini is available for keynotes, workshops, corporate events, retreats, and school engagements. Let's create an experience your audience will never forget.</p>

        <div className="bc-contact-links">
          <a href="mailto:alchemyvillage@thealchemyvillage.com" className="bc-contact-card" data-testid="bc-email-link">
            <span className="bc-contact-label">Email Directly</span>
            <span className="bc-contact-value">alchemyvillage@thealchemyvillage.com</span>
          </a>
          <Link to="/" className="bc-contact-card" data-testid="bc-site-link">
            <span className="bc-contact-label">Visit the Village</span>
            <span className="bc-contact-value">thealchemyvillage.com</span>
          </Link>
        </div>

        <p className="bc-contact-divider">Or fill out the form below and Cabrini's team will be in touch</p>

        <form className="bc-form" data-testid="bc-form" onSubmit={handleSubmit}>
          <input className="bc-input" placeholder="Your Name" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="bc-input-name" />
          <input className="bc-input" placeholder="Email Address" type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="bc-input-email" />
          <input className="bc-input" placeholder="Organization / Event" value={form.org} onChange={e => setForm({...form, org: e.target.value})} data-testid="bc-input-org" />
          <textarea className="bc-textarea" placeholder="Tell Cabrini About Your Event" rows={4} required value={form.message} onChange={e => setForm({...form, message: e.target.value})} data-testid="bc-input-message" />
          <button type="submit" className="village-btn village-btn--primary bc-submit" data-testid="bc-submit-btn">Send My Inquiry</button>
        </form>
      </div>
    </section>
  );
}
